/**
 * Avatar Component
 *
 * Displays user profile images with fallback to initials.
 */

import React from 'react';
import { Icon } from '@iconify/react';
import './Avatar.css';

const SIZES = {
  xs: 24,
  sm: 32,
  md: 40,
  lg: 56,
  xl: 80,
  '2xl': 120,
};

const Avatar = ({
  src,
  alt = '',
  name,
  size = 'md',
  shape = 'circle',
  status,
  className = '',
  onClick,
  ...props
}) => {
  const sizeValue = typeof size === 'number' ? size : SIZES[size] || SIZES.md;
  const [imageError, setImageError] = React.useState(false);

  const getInitials = (name) => {
    if (!name) return '';
    const words = name.trim().split(' ').filter(Boolean);
    if (words.length === 0) return '';
    if (words.length === 1) return words[0].charAt(0).toUpperCase();
    return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const showImage = src && !imageError;
  const initials = getInitials(name);

  const classNames = [
    'ds-avatar',
    `ds-avatar--${shape}`,
    status && `ds-avatar--status-${status}`,
    onClick && 'ds-avatar--clickable',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div
      className={classNames}
      style={{ width: sizeValue, height: sizeValue }}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      {...props}
    >
      {showImage ? (
        <img
          src={src}
          alt={alt || name || 'Avatar'}
          className="ds-avatar__image"
          onError={handleImageError}
        />
      ) : initials ? (
        <span
          className="ds-avatar__initials"
          style={{ fontSize: sizeValue * 0.4 }}
        >
          {initials}
        </span>
      ) : (
        <Icon
          icon="mdi:account"
          className="ds-avatar__icon"
          style={{ fontSize: sizeValue * 0.6 }}
        />
      )}

      {status && <span className="ds-avatar__status-indicator" />}
    </div>
  );
};

export default Avatar;
